/**
 * useTestCalls - the GM calls for a test; whoever is asked rolls it.
 *
 * `callFor(call)` (engine/narrative/callForTest) opens a call, kept as a
 * document while it waits (useOpenCallDocs). For each person named, by
 * `call.prompt` or the defaults (engine/roster/actedAs: everyone who acted
 * as them this session):
 *
 * - accounts are prompted: each gets the call as an ask (testAsk.ts). An
 *   ORDINARY prompt carries the call, the asked screen rolls it with its
 *   own dice and sends the result back as the answer (testAnswer.ts); this
 *   screen checks it is the one asked for and writes the line, credited to
 *   the roller (`by`). A SECRET prompt carries no call: Roll is a signal,
 *   and the dice are rolled HERE, so the result never exists over there.
 *   The first answer for a person stands; the other prompts are withdrawn.
 * - nobody is: rolled here at once.
 *
 * When the last person settles the call is CLOSED - stamped before the
 * group line and the riders, so a reload after it fires nothing twice -
 * then everything rolled is handed on in ONE list (`onResolved`), and the
 * document deleted.
 *
 * `openCalls` is what is still waiting, for the GM's panel: `rollFor` rolls
 * for someone who has not answered, `cancelCall` withdraws the rest. Both
 * withdraw the prompts, and a person is settled once whichever comes first.
 *
 * A prompt answered after this screen was reloaded is still taken: see
 * `useLateAnswers`.
 *
 * @param {object} args - roster, asks (useAsks), actingBy, actedBy, me ({ uid }),
 *   nameOf (uid -> a name, for the credit), sessionId, isGm, mp (multiplayer,
 *   for the documents), addRollNarrative, postSecret, unnaturalDosMode,
 *   assistBonus, onResolved (the call and every result, once, at the close).
 * @returns {{ openCalls, callFor, rollFor, cancelCall }}
 */
import { useCallback, useEffect, useRef } from 'react';
import { groupLine, resolveTestCall } from '../engine/narrative/callForTest';
import { readTestAsk, testAskOf } from '../engine/narrative/testAsk';
import { answerMatches, readTestAnswer } from '../engine/narrative/testAnswer';
import { closeCall, isDone, openCallOf, settleOne } from '../engine/narrative/openCall';
import { promptDefaults } from '../engine/roster/actedAs';
import useOpenCallDocs from './useOpenCallDocs';

/** The ask's kind on the wire. */
export const TEST_ASK = 'test';

/** The pending prompts for this person of this call, among the asks this seat is waiting on. */
function promptsFor(outgoing, callId, combatantId) {
  return outgoing.filter((a) => {
    if (a.kind !== TEST_ASK) return false;
    const prompt = readTestAsk(a.payload);
    return prompt?.callId === callId && prompt.combatantId === combatantId;
  });
}

/** Publish one entry: to the GM alone when the call is secret, to the log otherwise, credited when told. */
function publish(now, call, summary, structured, by = null) {
  if (call.secret) return now.postSecret(summary);
  const type = structured ? 'test-result' : 'info';
  now.addRollNarrative({ type, text: summary, structured, timestamp: Date.now(), ...(by ? { by } : {}) });
}

/** Roll one person of a call here, with this screen's dice and roster. */
function rollHere(now, call, combatantId) {
  const one = { ...call, who: [combatantId] };
  return resolveTestCall(one, now.roster, { unnaturalDosMode: now.unnaturalDosMode, assistBonus: now.assistBonus })[0];
}

/** Who to prompt for each person: the call's own say, else the defaults; nobody outside a session. */
function promptedFor(now, call) {
  if (!now.sessionId) return Object.fromEntries(call.who.map((cid) => [cid, []]));
  return call.prompt ?? promptDefaults(call.who, now.actedBy ?? {}, now.actingBy ?? {}, now.me?.uid ?? null);
}

/** The outcome a settlement carries: a result rolled here, one that came back, or nothing. */
const outcome = (result, by = null) => ({ result, by });

/** The last person settled: close, the group line, every result handed on once, the document gone. */
function finishIn(store, now, open) {
  store.put(closeCall(open, Date.now()));
  const line = groupLine(open.call, open.results);
  if (line) publish(now, open.call, line);
  if (open.results.length > 0) now.onResolved?.(open.call, open.results);
  store.remove(open.id);
}

/** One person done: their line published (when rolled), their prompts withdrawn, the call moved on. */
function settleIn(store, now, callId, combatantId, { result, by }) {
  const open = store.get(callId);
  const next = open && settleOne(open, combatantId, result);
  if (!next) return;
  if (result) publish(now, open.call, result.entry.summary, result.passed === null ? undefined : result.entry, by);
  for (const prompt of promptsFor(now.asks.outgoing, callId, combatantId)) now.asks.cancel(prompt.id);
  if (isDone(next)) finishIn(store, now, next);
  else store.put(next);
}

/** The settlement, idempotent: the first stands. */
function useSettle(store, live) {
  return useCallback(
    (callId, combatantId, what) => settleIn(store, live.current, callId, combatantId, what),
    [store, live],
  );
}

/** Is this a call the asked screen was never given (engine/narrative/testAsk `callForAsked`)? */
const keptHere = (call) => call.secret || !!call.against;

/** A prompt came back: the signal (roll here), a result (take it), or nothing (let go). */
function useAccept(store, live, settle) {
  return useCallback(
    (callId, combatantId, uid, answer) => {
      const now = live.current;
      const open = store.get(callId);
      if (!open) return;
      // Rolled HERE on the signal - and on ANYTHING sent back for a call the asked screen was never
      // given (secret, or opposed): a result for one of those is a console's, never the dice's.
      if (answer === true || (answer != null && keptHere(open.call))) {
        return settle(callId, combatantId, outcome(rollHere(now, open.call, combatantId)));
      }
      const came = readTestAnswer(answer);
      const taken = came && answerMatches({ callId, combatantId }, came) ? came.result : null;
      settle(callId, combatantId, taken ? outcome(taken, now.nameOf?.(uid) ?? null) : outcome(null));
    },
    [store, live, settle],
  );
}

/**
 * The call with everyone nobody plays ALREADY ROLLED, their lines published, before it is stored at
 * all. It used to be written with everyone waiting and again a moment later with those rolls in: a
 * GM who reloaded between the two writes came back to "Waiting on Kalypsia", who had rolled, and the
 * group line never came (E2E on PocketBase, 2026-10-06).
 */
function rolledAtOnce(now, open, atOnce) {
  let next = open;
  for (const c of atOnce) {
    const result = rollHere(now, open.call, c.id);
    if (result) publish(now, open.call, result.entry.summary, result.passed === null ? undefined : result.entry);
    next = settleOne(next, c.id, result) ?? next;
  }
  return next;
}

/** Open a call: roll here whoever nobody plays, store it once, then a prompt per account asked. */
function useCallFor(store, live, accept) {
  return useCallback(
    (call) => {
      const now = live.current;
      const asked = call.who.map((cid) => now.roster.find((c) => c.id === cid)).filter(Boolean);
      if (asked.length === 0) return;
      const id = `call-${Date.now()}`;
      const prompted = promptedFor(now, call);
      const uidsOf = (c) => prompted[c.id] ?? [];
      const atOnce = asked.filter((c) => uidsOf(c).length === 0);
      const open = rolledAtOnce(now, openCallOf(id, call, prompted, Date.now()), atOnce);
      if (isDone(open)) return finishIn(store, now, open);
      store.put(open);
      for (const c of asked) {
        for (const uid of uidsOf(c)) {
          now.asks.ask(TEST_ASK, { uid }, testAskOf(id, c, call)).then((answer) => accept(id, c.id, uid, answer));
        }
      }
    },
    [store, live, accept],
  );
}

const NO_ASKS = [];

/**
 * ANSWERS NOBODY HERE WAS WAITING FOR (2026-10-06). A prompt is awaited by a
 * promise, and a promise dies with the page: the GM reloaded mid-call, the
 * call came back from its document, and the player's roll a minute later
 * was taken by nobody. `asks.unclaimed` is every ask of this seat that
 * ended without a waiter; the ones that are test prompts are accepted here
 * exactly as their promise would have (a withdrawn one lets the person go),
 * once the stored calls have been read and the person is on the roster, and
 * then claimed. One for a call that is no longer open is only cleared away.
 * One made after this screen came up is left alone: it is another screen's.
 */
function useLateAnswers({ asks, roster }, store, accept) {
  const unclaimed = asks?.unclaimed ?? NO_ASKS;
  const claim = asks?.claim;
  const { ready, get } = store;
  // Only a screen that came up AFTER an ask was made can have lost its wait for it: one that was
  // already up either holds the promise or is another of the GM's screens, and must leave it.
  const upSince = useRef(Date.now()).current;
  useEffect(() => {
    if (!ready || !claim) return;
    const mine = unclaimed.filter((a) => a.kind === TEST_ASK && a.createdAt < upSince);
    for (const ask of mine) adopt(ask, { get, roster, accept, claim });
  }, [unclaimed, claim, ready, get, accept, roster, upSince]);
}

/** Take one late answer, clear away one whose call is gone, or leave one whose person is not here yet. */
function adopt(ask, { get, roster, accept, claim }) {
  const late = lateFor(ask, get);
  // The person is not on this screen's roster yet (it hydrates after the calls are read): a
  // signal rolled now would settle them with nothing. Wait; the roster's arrival runs this again.
  if (late && !roster.some((c) => c.id === late.combatantId)) return;
  if (late) accept(late.callId, late.combatantId, late.uid, late.answer);
  claim(ask.id);
}

/** The late answer, when its call is still open here; null for one that is only to be cleared away. */
function lateFor(ask, get) {
  const late = lateAnswer(ask);
  return late && get(late.callId) ? late : null;
}

/** An ended prompt as its promise would have delivered it: whose, for which call, and the answer (null = let go). */
function lateAnswer(ask) {
  const prompt = readTestAsk(ask.payload);
  if (!prompt) return null;
  const answer = ask.status === 'answered' ? ask.answer : null;
  return { callId: prompt.callId, combatantId: prompt.combatantId, uid: ask.to?.uid ?? null, answer };
}

export default function useTestCalls(args) {
  const live = useRef(args);
  live.current = args;
  const store = useOpenCallDocs({ sessionId: args.sessionId, isGm: args.isGm, mp: args.mp });
  const settle = useSettle(store, live);
  const accept = useAccept(store, live, settle);
  const callFor = useCallFor(store, live, accept);
  useLateAnswers(args, store, accept);

  const rollFor = useCallback(
    (callId, combatantId) => {
      const open = store.get(callId);
      if (open) settle(callId, combatantId, outcome(rollHere(live.current, open.call, combatantId)));
    },
    [store, settle],
  );
  const cancelCall = useCallback(
    (callId) => {
      const open = store.get(callId);
      for (const combatantId of open?.waiting ?? []) settle(callId, combatantId, outcome(null));
    },
    [store, settle],
  );

  return { openCalls: store.openCalls, callFor, rollFor, cancelCall };
}
